/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, May 2020
 */
// @ts-ignore
import qs from 'query-string';
import { forOwn } from '../../../core/lib';

/**
 * A query of query string.
 */
export type Query = Record<string, QueryParameter>;

/**
 * Query parameter value.
 */
export type QueryParameter = string | number | boolean | QueryParameter[];

/**
 * A utility for parsing and manipulating query strings.
 */
export class URLSearch {
    private empty = true;
    private parsed: Query;

    /**
     * Create instance.
     */
    constructor();
    /**
     * Create instance.
     *
     * @param query A key-value mapping of query parameters.
     */
    constructor(query: Query);
    /**
     * Parse a query string.
     *
     * @param queryString String to parse.
     */
    constructor(queryString: string);
    constructor(queryOrQueryString?: Query | string) {
        if (typeof queryOrQueryString === 'string') {
            this.parsed = qs.parse(queryOrQueryString, {
                sort: false
            }) as Query;
        } else {
            this.parsed = queryOrQueryString ?? {};
        }

        if (Object.keys(this.parsed).length > 0) {
            this.empty = false;
        }
    }

    /**
     * Check if the query string has a given parameter.
     *
     * @param name Parameter name.
     * @returns True if the parameter is in the query string.
     */
    public has(name: string): boolean {
        return name in this.parsed;
    }

    /**
     * Add a value to the query string.
     *
     * @param name Name of the parameter.
     * @param value Value of the parameter.
     */
    public add(name: string, value: QueryParameter): void {
        const previousValue = this.parsed[name];

        if (Array.isArray(previousValue)) {
            previousValue.push(value);
            this.parsed[name] = previousValue;
        } else {
            this.parsed[name] = value;
        }

        if (this.empty) this.empty = false;
    }

    /**
     * Get the value of a query string parameter.
     *
     * @param name Parameter name.
     * @returns Value of the query parameter.
     */
    public get<T extends QueryParameter>(name: string): T {
        const value = this.parsed[name];
        if (value == null) {
            throw new Error(`Missing query string parameter \`${name}\``);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return value as any;
    }

    /**
     * Merge values of another QueryString into this one.
     *
     * @param search A QueryString instance to merge.
     */
    public merge(search: URLSearch): void {
        search.forEach((value, name) => {
            this.add(name, value);
        });
    }

    /**
     * Check if the Search object doesn't contain any params.
     *
     * @returns True if the Search object is empty.
     */
    public isEmpty(): boolean {
        return this.empty;
    }

    /**
     * Loop through all query string parameters.
     *
     * @param iterator A callback function called for every parameter.
     */
    public forEach(iterator: (value: QueryParameter, name: string) => void): void {
        forOwn(this.parsed, iterator);
    }

    /**
     * Serialize the query string.
     */
    public toString(): string {
        return qs.stringify(this.parsed, {
            sort: false,
            strict: false
        });
    }
}



// WEBPACK FOOTER //
// ./src/packages/http/lib/URL/URLSearch.ts